import { Button, styled, Typography } from '@mui/material';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Form } from '~/shared/form';
import { Col, Row } from '~/view/view.box';
import { PromotionCommonDto, PromotionDraggableListCard, PromotionListItemAction } from './promotion.list.item';

export interface PromotionDraggableListProps<T extends PromotionCommonDto> {
	label: string;
	field: Form.Field<T[]>;
	max?: number;
	onAdd?: () => void;
	onEdit?: PromotionListItemAction<T>;
	onDelete?: PromotionListItemAction<T>;
}

export const PromotionDraggableList = observer(
	<T extends PromotionCommonDto>({ label, field, max = 5, onAdd, ...actions }: PromotionDraggableListProps<T>) => {
		const { value } = field;
		const count = value?.length ?? 0;

		const handleDragEnd = useCallback(
			(e: DropResult) => {
				if (!e.destination || !value) return;
				const arr = Array.from(value);
				const [removed] = arr.splice(e.source.index, 1);
				arr.splice(e.destination.index, 0, removed);
				arr.forEach((item, idx) => runInAction(() => (item.order = idx + 1)));
				field.set(arr);
			},
			[value, field],
		);

		return (
			<Col flex={1}>
				<Row alignItems="center" justifyContent="space-between">
					<Typography variant="caption" color="textSecondary">
						{label} ({count}/{max})
					</Typography>
					<Button size="small" color="primary" onClick={() => onAdd?.()} disabled={count >= max || !onAdd}>
						Add fragrance
					</Button>
				</Row>
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="draggable-list" direction="horizontal">
						{provided => (
							<DroppableList {...provided.droppableProps} ref={provided.innerRef}>
								{!value?.length && (
									<Col flex={1}>
										<Typography variant="body2" color="textSecondary" textAlign="center">
											No fragrances added yet.
										</Typography>
									</Col>
								)}
								{value?.map((item, idx) => {
									return (
										<Draggable
											index={idx}
											draggableId={`item-${item.fragranceId}-${idx}`}
											key={`item-${item.fragranceId}`}
										>
											{(provided, snapshot) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													style={{
														...provided.draggableProps.style,
														opacity: snapshot.isDragging ? 0.7 : 1,
													}}
												>
													<PromotionDraggableListCard item={item} actions={actions} />
												</div>
											)}
										</Draggable>
									);
								})}

								{provided.placeholder}
							</DroppableList>
						)}
					</Droppable>
				</DragDropContext>
			</Col>
		);
	},
);

const DroppableList = styled('div')({
	minHeight: 275,
	padding: 8,
	display: 'flex',
	flexDirection: 'row',
	gap: 8,
	overflowX: 'auto',
	border: '1px solid #ccc',
});
